import type { KeyboardEvent } from 'react';

/**
 * Šípky medzi záložkami (roving tabindex) podľa vzoru WAI-ARIA Tabs.
 *
 * Tabulátor vstúpi do lišty iba raz — na vybranú záložku — a ďalej sa chodí
 * šípkami. Keby mala každá záložka vlastný tabindex, prešiel by človek z
 * klávesnice cez desať tlačidiel, kým by sa dostal k obsahu.
 *
 * `activate: false` (manuálna aktivácia) iba presúva fokus; vyberie sa až
 * Enterom alebo medzerníkom. Hodí sa tam, kde prepnutie niečo stojí — v detaile
 * projektu by šípka cez Tickety naťahovala celú dosku len preto, že sa cez ňu
 * prechádzalo.
 */
export const handleTablistKeyDown = (
  event: KeyboardEvent<HTMLElement>,
  { activate = true, orientation = 'horizontal' }: { activate?: boolean; orientation?: 'horizontal' | 'vertical' } = {},
): void => {
  const prev = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
  const next = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
  if (![prev, next, 'Home', 'End'].includes(event.key)) {
    return;
  }
  const tabs = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])'),
  );
  if (tabs.length === 0) {
    return;
  }
  const current = tabs.indexOf(document.activeElement as HTMLElement);
  let target: number;
  if (event.key === 'Home') {
    target = 0;
  } else if (event.key === 'End') {
    target = tabs.length - 1;
  } else if (event.key === next) {
    target = current < 0 ? 0 : (current + 1) % tabs.length;
  } else {
    target = current < 0 ? tabs.length - 1 : (current - 1 + tabs.length) % tabs.length;
  }
  event.preventDefault();
  const el = tabs[target];
  el.focus();
  // Úzka lišta sa posúva do strany; zaostrená záložka nesmie ostať mimo.
  el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  if (activate) {
    el.click();
  }
};
